import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useUserPrefixDicom } from "@/features/imagings/hooks/useImaging";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";
import { Check, Copy, Info, Loader2 } from "lucide-react";
import { useState } from "react";

interface SelectedUserInfoProps {
  user: User;
}

export function SelectedUserInfo({ user }: SelectedUserInfoProps) {
  const { data: prefix, isLoading: isLoadingPrefix } = useUserPrefixDicom(user.uid);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const infoItems = [
    { label: "Email", value: user.email, key: "email" },
    { label: "User ID (UID)", value: user.uid, key: "uid" },
    {
      label: "Prefijo DICOM (UUID)",
      value: prefix,
      key: "prefix",
      isLoading: isLoadingPrefix,
    },
  ];

  return (
    <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          Información Adicional: {getUserFullName(user)}
        </CardTitle>
        <CardDescription>
          Detalles de la institución o médico seleccionado. Puedes copiar los campos
          haciendo clic en el icono.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {infoItems.map((item) => (
            <div key={item.key} className="space-y-1">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                {item.label}
              </span>
              <div className="flex items-center gap-2 max-w-full">
                {item.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400 h-9">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Cargando...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 bg-white dark:bg-gray-800 border rounded px-3 py-2 text-sm font-mono truncate border-gray-200 dark:border-gray-700 min-h-[36px] flex items-center">
                      {item.value || "No disponible"}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 shrink-0 hover:bg-blue-100 dark:hover:bg-blue-900/30 tap-target bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                      onClick={() => copyToClipboard(item.value || "", item.key)}
                      disabled={!item.value}
                      title={`Copiar ${item.label}`}
                    >
                      {copiedField === item.key ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
